import React, { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { Avatar, Tooltip } from "@fluentui/react-components";
import { Spinner } from "react-bootstrap";
import Table from "../../components/controls/tables/Table";
import Modal from "../../components/controls/modal/Modal";
import { ColumnConfigProvider } from "../../components/controls/tables/ColumnConfigProvider";
import UserSelect from "../../components/userSelect/UserSelect";
import HoverCard from "../../components/hoverCard/HoverCard";
import { generalActions } from "../../store/actions";
import reshuffleservice from "../../store/services/reshuffleRequests.service";
import searchService from "../../store/services/search.service";
import {
    calculatePageSize,
    getEditPermission,
    formatMonthYear,
    getReshuffleStatusLabel,
    getReshuffleStatusColorClass,
    UserSearchRefType,
    formatPeriodMonthYear,
} from "../../helpers/utils";
import ReshuffleSummaryPreview from "./ReshuffleSummaryPreview";
import styles from "./ReshuffleRequest.module.scss";
import RichTextEditor from "../../components/richTextEditor/RichTextEditor";
import EntityFileUpload from "../../components/controls/fileUpload/EntityFileUpload";
import DropDown from "../../components/controls/dropdown/DropdownFluent";
import Input from "../../components/controls/input/customInput/CustomInput";
import Searchable from "../../components/controls/select/CustomSelect";
import _, { debounce, has } from "lodash";
import moment from "moment";
import { use } from "react";
import { Check, X } from "lucide-react";
import { getAccountCurrency, displayDecimalFormatted } from "../../utils/systemSettings";
import { ArrowLeft } from "../../modules/default-svg";
import { mode } from "crypto-js";

const now = moment();
const currentYear = now.year();
const currentMonth = now.month() + 1;
const setting = JSON.parse(localStorage.getItem("settings"));

const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const YEAR_OPTIONS = Array.from({ length: 15 }, (_, i) => currentYear - 5 + i);


const ReshuffleRequest = ({
    projectID,
    allReshuffleRequests,
    pagination,
    loading,
    pageLoading,
    getReshuffleRequests,
    createReshuffleRequest,
    updateReshuffleRequest,
    deleteReshuffleRequest,
    submitReshuffleRequest,
    approveReshuffleRequest,
    rejectReshuffleRequest,
    executeReshuffleRequest,
    cancelReshuffleRequest,
}) => {
    const dispatch = useDispatch();
    const canEdit = getEditPermission("Project");

    // ── Table data & pagination ────────────────────────────────────────────────────────
    const [data, setData] = useState([]);
    const [perPageItems] = useState(calculatePageSize());
    const [pageNum, setPageNum] = useState(1);
    const [tableSearch, setTableSearch] = useState(null);
    const [deleteId, setDeletedId] = useState(null);

    // ── Modal / wizard state ────────────────────────────────────────────────────
    // modalMode drives what formBody() renders (inside Table.jsx's modal)
    const [modalMode, setModalMode] = useState(""); // '' | 'create' | 'edit'
    const [wizardStep, setWizardStep] = useState(1);      // 1 | 2 | 3 (create only)
    const [selectedRowId, setSelectedRowId] = useState(null);

    // Direct modal (view / approve / reject / summary — outside Table.jsx modal)
    const [showDirectModal, setShowDirectModal] = useState(false);
    const [directModalMode, setDirectModalMode] = useState("view"); // 'view'|'approve'|'reject'|'summary'
    const [directModalRow, setDirectModalRow] = useState(null);
    const [viewDetail, setViewDetail] = useState(null);
    const [summaryData, setSummaryData] = useState(null);
    const [directModalLoading, setDirectModalLoading] = useState(false);

    // ── Step 1 form fields ──────────────────────────────────────────────────────
    const [requestId, setRequestId] = useState(null);
    const [targetResource, setTargetResource] = useState(null);
    const [requestedHours, setRequestedHours] = useState("");
    const [periodStartYear, setPeriodStartYear] = useState(currentYear);
    const [periodStartMonth, setPeriodStartMonth] = useState(currentMonth);
    const [periodEndYear, setPeriodEndYear] = useState(currentYear);
    const [periodEndMonth, setPeriodEndMonth] = useState(currentMonth);
    const [notes, setNotes] = useState(null);
    const [status, setStatus] = useState(0);
    const [attachments, setAttachments] = useState([]);
    const [changeRequest, setChangeRequest] = useState(null);

    // Change Request search
    const [changeRequests, setChangeRequests] = useState([]);


    // ── Step 2 state ────────────────────────────────────────────────────────────
    const [options, setOptions] = useState(null);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [selectedOptionIdx, setSelectedOptionIdx] = useState(null);
    const [editableItems, setEditableItems] = useState([]);
    const [validation, setValidation] = useState(null);
    const [validationLoading, setValidationLoading] = useState(false);

    // ── Review (approve/reject) ─────────────────────────────────────────────────
    const [reviewNotes, setReviewNotes] = useState("");
    const [reviewNotesError, setReviewNotesError] = useState("");

    // ── Form errors ─────────────────────────────────────────────────────────────
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (periodStartYear > periodEndYear) {
            setPeriodEndYear(periodStartYear);
        }
    }, [periodStartYear]);

    useEffect(() => {
        if (periodStartYear === periodEndYear && periodStartMonth > periodEndMonth) {
            setPeriodEndMonth(periodStartMonth);
        }
    }, [periodStartMonth, periodEndMonth, periodStartYear, periodEndYear]);

    useEffect(() => {
        if (projectID) fetchData(1);
    }, [projectID]);

    useEffect(() => {
        fetchData();
    }, [pageNum, perPageItems]);

    const fetchData = (pageNumber, pageSize, searchTerm, forceClearSearch = false) => {
        if (!projectID) {
            setData([]);
            return;
        }
        const payload = {
            projectId: projectID,
            page: pageNumber ?? pageNum,
            size: pageSize ?? perPageItems,
            search: forceClearSearch ? null : (searchTerm ?? tableSearch)
        };


        dispatch(getReshuffleRequests(payload));
    };

    useEffect(() => {
        if (allReshuffleRequests && allReshuffleRequests.length > 0) {
            if (pageNum === 1) {
                setData(
                    allReshuffleRequests
                        .filter((item) => item.id !== deleteId)
                        .sort((a, b) => {
                            return b.isFlagged - a.isFlagged;
                        })
                );
            }
            else {
                setData((prevData) => {
                    const newMap = new Map(
                        allReshuffleRequests.map((item) => [item.id, item])
                    );

                    const updatedPrevData = prevData.map((item) =>
                        newMap.has(item.id) ? newMap.get(item.id) : item
                    );

                    const prevDataIds = new Set(prevData.map((item) => item.id));
                    const newItems = allReshuffleRequests.filter(
                        (shift) => !prevDataIds.has(shift.id)
                    );

                    return [...updatedPrevData, ...newItems]
                        .filter((item) => item.id !== deleteId)
                        .sort((a, b) => b.isFlagged - a.isFlagged);
                });
            }
        } else if (allReshuffleRequests && allReshuffleRequests.length === 0) {
            setData([]);
        }
    }, [allReshuffleRequests]);

    // ── Change request search (debounced) ───────────────────────────────────────
    const changRequestSearchdebounced = useRef(
        debounce((value, params) => searchChangRequestHandler(value, params), 500)
    );

    const changRequestSearch = useCallback((value, params) => {
        changRequestSearchdebounced.current(value, params);
    }, []);

    const searchChangRequestHandler = (value, params) => {
        if (!params.projectId) {
            setChangeRequests([]);
            return;
        }
        searchService
            .ChangeRequests(value, params)
            .then((res) => {
                const transformedArray = res.map((obj) => ({
                    label: obj.name,
                    value: obj.id,
                }));
                setChangeRequests(transformedArray);
            })
            .catch(() => {
                setChangeRequests([]);
            });
    };

    // ── Period validation ───────────────────────────────────────────────────────
    const isPeriodValid = () => {
        if (periodStartYear > periodEndYear) return false;
        if (periodStartYear === periodEndYear && periodStartMonth > periodEndMonth) return false;
        return true;
    };

    // Generate array of {year, month} for the configured period
    const generatePeriodMonths = () => {
        const months = [];
        let y = periodStartYear;
        let m = periodStartMonth;
        const endY = periodEndYear;
        const endM = periodEndMonth;
        while (y < endY || (y === endY && m <= endM)) {
            months.push({ name: formatMonthYear(y, m), value: `${y}-${m}` });
            m++;
            if (m > 12) { m = 1; y++; }
        }
        return months;
    };

    // ── Resource pool helpers ───────────────────────────────────────────────────
    const getPoolForMonth = (year, month) =>
        (options?.resourcePool || []).filter((r) => r.year === year && r.month === month);

    const getResourceDataFromPool = (pool, donorResourceId) => {
        const sel = (pool || []).find((r) => r.resource?.id === donorResourceId);
        return sel ? sel : null;
    }

    // ── Validation row helpers ──────────────────────────────────────────────────
    const getRowErrors = (donorResourceId, year, month) =>
        (validation?.errors || []).filter((e) => e.resourceId === donorResourceId && e.year === year && e.month === month);
    const getRowWarnings = (donorResourceId, year, month) =>
        (validation?.warnings || []).filter((w) => w.resourceId === donorResourceId && w.year === year && w.month === month);
    const getGlobalErrors = () =>
        (validation?.errors || []).filter((e) => e.isGlobal);

    // ── Build create DTO ────────────────────────────────────────────────────────
    const buildDto = () => {
        const items = editableItems.map((item) => ({
            year: item.year,
            month: item.month,
            donorResourceId: item.donorResource?.id || null,
            hoursRequested: parseFloat(item.targetHoursAdded) || 0,
        }));

        const totalHoursRequested = items.reduce(
            (sum, item) => sum + item.hoursRequested,
            0
        );
        return {
            ResourceId: targetResource?.id,
            requestedHours: parseFloat(totalHoursRequested) || 0,
            periodStartYear,
            periodStartMonth,
            periodEndYear,
            periodEndMonth,
            notes,
            changeRequestId: changeRequest?.value || null,
            items: items
        };
    };


    // ── Fetch options (Step 1 → 2) ──────────────────────────────────────────────
    const handleGetOptions = async () => {
        const res = targetResource;
        const hrs = requestedHours;
        const sY = periodStartYear;
        const sM = periodStartMonth;
        const eY = periodEndYear;
        const eM = periodEndMonth;

        if (!res) {
            setFormErrors((e) => ({ ...e, targetResource: "Target resource is required" }));
            return;
        }
        else {
            setFormErrors((e) => ({ ...e, targetResource: null }));
        }
        if (!hrs || parseFloat(hrs) <= 0) {
            setFormErrors((e) => ({ ...e, requestedHours: "Hours must be greater than 0" }));
            return;
        }
        else {
            setFormErrors((e) => ({ ...e, requestedHours: null }));
        }
        if (!isPeriodValid()) {
            setFormErrors((e) => ({ ...e, period: "End period must be equal to or greater than start period" }));
            return;
        }
        else {
            setFormErrors((e) => ({ ...e, period: null }));
        }
        setFormErrors({});
        setOptionsLoading(true);
        try {
            const data = await reshuffleservice.getReshuffleOptions(projectID, {
                resourceId: res.id,
                requestedHours: parseFloat(hrs),
                periodStartYear: sY,
                periodStartMonth: sM,
                periodEndYear: eY,
                periodEndMonth: eM,
            });
            setOptions(data);
            setValidation(null);
            setEditableItems([]);
            setSelectedOptionIdx(null);
            if (modalMode === "create")
                setWizardStep(2);
        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load reshuffle options.", type: "error" }));
        } finally {
            setOptionsLoading(false);
        }
    };

    // ── Select system option ────────────────────────────────────────────────────
    const handleSelectOption = (opt) => {
        setSelectedOptionIdx(opt.optionIndex);
        setEditableItems(
            opt.items.map((item, i) => ({
                tempId: `opt-${i}`,
                year: item.year,
                month: item.month,
                donorResource: item.donorResource,
                donorHoursReduced: item.donorHoursReduced,
                targetHoursAdded: item.targetHoursAdded,
            }))
        );
        setValidation(null);
    };

    // ── Validate (Step 2 → 3) ───────────────────────────────────────────────────
    const handleValidate = async () => {
        if (editableItems.length === 0) {
            const result = { errors: [{ isValid: false, isGlobal: true, message: "At least one donor item is required" }] };
            setValidation(result);
            return;
        }
        setValidationLoading(true);
        try {
            const result = await reshuffleservice.validateReshuffleRequest(projectID, buildDto());
            setValidation(result);
            if (result.isValid) setWizardStep(3);
        } catch {
            dispatch(generalActions.showToast({ title: "Validation failed. Please try again.", type: "error" }));
        } finally {
            setValidationLoading(false);
        }
    };

    // ── Confirm & Create (Step 3) ───────────────────────────────────────────────
    const handleConfirmCreate = async () => {
        setValidationLoading(true);
        try {
            const dto = buildDto();
            const result = await reshuffleservice.validateReshuffleRequest(projectID, dto);
            if (!result.isValid) {
                setValidation(result);
                setWizardStep(2);
                setValidationLoading(false);
                return;
            }
            createReshuffleRequest({ projectId: projectID, payload: dto, size: perPageItems });
            // modalLoading will toggle → Table.jsx useEffect closes modal automatically
        } catch {
            dispatch(generalActions.showToast({ title: "Something went wrong. Please try again.", type: "error" }));
            setValidationLoading(false);
        }
    };

    // ── Save handler (called by Table.jsx modal save button) ────────────────────
    const handleSave = async () => {
        if (modalMode === "edit") {
            if (editableItems.length === 0) {
                dispatch(generalActions.showToast({ title: "Add at least one item.", type: "error" }));
                return;
            }

            if (editableItems.length === 0) {
                const result = { errors: [{ isValid: false, isGlobal: true, message: "At least one donor item is required" }] };
                setValidation(result);
                return;
            }
            setValidationLoading(true);
            try {
                const result = await reshuffleservice.validateReshuffleRequest(projectID, buildDto());
                setValidation(result);
                if (!result.isValid)
                    return;
            } catch {
                dispatch(generalActions.showToast({ title: "Validation failed. Please try again.", type: "error" }));
            } finally {
                setValidationLoading(false);
            }
            const items = editableItems.map((item) => ({
                year: item.year,
                month: item.month,
                donorResourceId: item.donorResource?.id || null,
                hoursRequested: parseFloat(item.targetHoursAdded) || 0,
            }));

            updateReshuffleRequest({
                projectId: projectID,
                id: requestId,
                payload: {
                    notes,
                    changeRequestId: changeRequest?.value || null,
                    items: items,
                },
                size: perPageItems,
            });
        } else if (wizardStep === 1) {
            handleGetOptions();
        } else if (wizardStep === 2) {
            handleValidate();
        } else if (wizardStep === 3) {
            handleConfirmCreate();
        }
    };

    //delete handler
    const handleDelete = (selectedRow) => {
        const id = selectedRow?.id;
        deleteReshuffleRequest({ projectId: projectID, id: selectedRow?.id, size: perPageItems })
        setDeletedId(id);
    };
    // ── Row select callback (intercepted from Table.jsx handleEditAction) ────────
    const handleCreate = (val) => {
        resetForm();
        if (val)
            setModalMode("create");
    };

    useEffect(() => {
        if (selectedRowId && modalMode === "edit") {
            handleEditSelect();
        }
    }, [selectedRowId]);

    const handleRowSelect = async (row) => {
        if (!row || !row.id) {
            return;
        }
        else
            setSelectedRowId(row?.id);
    }

    const handleEditSelect = () => {
        resetForm();
        if (!selectedRowId) {
            return;
        }

        // Edit mode
        setModalMode("edit");
        setDirectModalLoading(true);
        try {
            reshuffleservice.getSingleReshuffleRequest(projectID, selectedRowId).then((data) => {

                setRequestId(data.id);
                setOptions(null);
                setValidation(null);
                setTargetResource(data.targetResource);
                setRequestedHours(data.requestedHours);
                setPeriodStartYear(data.periodStartYear);
                setPeriodStartMonth(data.periodStartMonth);
                setPeriodEndYear(data.periodEndYear);
                setPeriodEndMonth(data.periodEndMonth);
                setStatus(data.status);
                setNotes(data.notes);
                setChangeRequest(data.changeRequest);
                setAttachments(data.attachments || []);
                if (data.changeRequest) {
                    setChangeRequest({
                        value: data.changeRequest.id,
                        label: data.changeRequest.name,
                    });
                }
                if (data.items && data.items.length > 0) {
                    setEditableItems(
                        data.items.map((item, i) => ({
                            tempId: item.id,
                            year: item.year,
                            month: item.month,
                            donorResource: item.donorResource,
                            donorHoursReduced: item.donorHoursReduced,
                            donorForecastHourlyCostRate: item.donorForecastHourlyCostRate,
                            targetHoursAdded: item.targetHoursAdded,
                            targetForecastHourlyCostRate: item.targetForecastHourlyCostRate,
                        }))
                    );
                }
            });

        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load details.", type: "error" }));
            setShowDirectModal(false);
        } finally {
            setDirectModalLoading(false);
        }
    };

    const resetForm = () => {
        setModalMode("");
        setWizardStep(1);
        setSelectedRowId(null);
        setRequestId(null);
        setTargetResource(null);
        setRequestedHours("");
        setPeriodStartYear(currentYear);
        setPeriodStartMonth(currentMonth);
        setPeriodEndYear(currentYear);
        setPeriodEndMonth(currentMonth);
        setNotes(null);
        setChangeRequest(null);
        setChangeRequests([]);
        setOptions(null);
        setEditableItems([]);
        setValidation(null);
        setSelectedOptionIdx(null);
        setFormErrors({});
        setValidationLoading(false);
        setOptionsLoading(false);
        setStatus(0);
        setAttachments([]);
    };

    // ── Editable item helpers ───────────────────────────────────────────────────
    const addEditableItem = () => {
        setValidation(null);
        setEditableItems((items) => [
            ...items,
            {
                tempId: `new-${Date.now()}`,
                year: periodStartYear,
                month: periodStartMonth,
                donorResource: null,
                donorHoursReduced: "",
                targetHoursAdded: "",
            },
        ]);
    };

    const removeEditableItem = (tempId) => {
        setEditableItems((items) => items.filter((i) => i.tempId !== tempId));
    };

    const updateEditableItem = (tempId, field, value) => {
        setEditableItems((items) =>
            items.map((i) => (i.tempId === tempId ? { ...i, [field]: value } : i))
        );
    };

    // ── Direct modal actions (view / summary / approve / reject / submit / execute / cancel) ──
    const handleAction = (action, row) => {
        setDirectModalRow(row);
        switch (action) {
            case "view":
                setDirectModalMode("view");
                setShowDirectModal(true);
                fetchViewDetail(row.id);
                break;
            case "summary":
                setDirectModalMode("summary");
                setShowDirectModal(true);
                fetchSummary(row.id);
                break;
            case "approve":
                setDirectModalMode("approve");
                setReviewNotes("");
                setReviewNotesError("");
                setShowDirectModal(true);
                break;
            case "reject":
                setDirectModalMode("reject");
                setReviewNotes("");
                setReviewNotesError("");
                setShowDirectModal(true);
                break;
            case "submit":
                submitReshuffleRequest({ projectId: projectID, id: row.id, size: perPageItems });
                break;
            case "execute":
                executeReshuffleRequest({ projectId: projectID, id: row.id, size: perPageItems });
                break;
            case "cancel":
                cancelReshuffleRequest({ projectId: projectID, id: row.id, size: perPageItems });
                break;
            default:
                break;
        }
    };

    const fetchViewDetail = async (id) => {
        setDirectModalLoading(true);
        try {
            const data = await reshuffleservice.getSingleReshuffleRequest(projectID, id);
            setViewDetail(data);
        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load details.", type: "error" }));
            setShowDirectModal(false);
        } finally {
            setDirectModalLoading(false);
        }
    };

    const fetchSummary = async (id) => {
        setDirectModalLoading(true);
        try {
            const data = await reshuffleservice.getReshuffleSummary(projectID, id);
            setSummaryData(data);
        } catch {
            dispatch(generalActions.showToast({ title: "Failed to load summary.", type: "error" }));
            setShowDirectModal(false);
        } finally {
            setDirectModalLoading(false);
        }
    };

    const closeDirectModal = () => {
        setShowDirectModal(false);
        setDirectModalRow(null);
        setViewDetail(null);
        setSummaryData(null);
        setReviewNotes("");
        setReviewNotesError("");
    };

    const handleApproveConfirm = () => {
        approveReshuffleRequest({ projectId: projectID, id: directModalRow.id, reviewNotes, size: perPageItems });
        closeDirectModal();
    };

    const handleRejectConfirm = () => {
        if (!reviewNotes.trim()) {
            setReviewNotesError("Review notes are required for rejection.");
            return;
        }
        rejectReshuffleRequest({ projectId: projectID, id: directModalRow.id, reviewNotes, size: perPageItems });
        closeDirectModal();
    };

    // ── Modal title / save label ────────────────────────────────────────────────
    const getSaveBtnLabel = () => {
        if (modalMode === "edit") return pageLoading ? "Saving…" : "Save";
        if (wizardStep === 1) return optionsLoading ? "Loading…" : "Get Options";
        if (wizardStep === 2) return validationLoading ? "Validating…" : "Next";
        return validationLoading ? "Creating…" : "Create";
    };

    const isSaveBtnDisabled = () => {
        if (modalMode === "edit" && pageLoading) return true;
        if (wizardStep === 1 && optionsLoading) return true;
        if ((wizardStep === 2 || wizardStep === 3) && validationLoading) return true;
        return false;
    };

    // ─────────────────────────────────────────────────────────────────────────────
    // FORM BODY (rendered inside Table.jsx's Modal)
    // ─────────────────────────────────────────────────────────────────────────────
    const renderUserHoverCell = (user, size = 24) => {
        if (!user) return null;
        return (
            <HoverCard
                HoverCardTrigger={
                    <div className={styles.userCell}>
                        <Avatar
                            name={user.name}
                            image={{ src: user.image }}
                            color="colorful"
                            size={20} />
                        <span>{user.name}</span>
                    </div>
                }
                AvatarId={user.id}
                AvatarName={user.name}
                AvatarImage={user.image}
                AvatarDesignation={user.title}
                AvatarEmail={user.email}
                AvatarPhone={user.phoneNumber}
                AvatarIsExternal={user.isExternal}
            />
        );
    };

    // ── Step 1 ──────────────────────────────────────────────────────────────────
    const renderStep1 = () => (
        <div className={styles.wizardStep}>
            <div className={styles.stepIndicator}>
                <div className={`${styles.stepDot} ${styles.active}`}>1</div>
                <div className={styles.stepLine} />
                <div className={styles.stepDot}>2</div>
                <div className={styles.stepLine} />
                <div className={styles.stepDot}>3</div>
            </div>


            {/* Target Resource */}
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                    Target Resource <span className={styles.required}>*</span>
                </label>
                <UserSelect
                    multiple={false}
                    refType={UserSearchRefType.Project}
                    refId={projectID}
                    value={targetResource}
                    onChange={setTargetResource}
                    label=""
                    buttonLabel="Select Resource"
                    selectedUsersDisplayMode="full"
                    isRequired={true}
                    className={"mt-1"}
                />
                {formErrors.targetResource && <p className={styles.errorText}>{formErrors.targetResource}</p>}

            </div>

            {/* Period */}
            <div className={styles.formRow3}>
                {/* Requested Hours */}
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>
                        Hours needed per month <span className={styles.required}>*</span>
                    </label>
                    <Input
                        inputwd="flex-1"
                        value={requestedHours}
                        type="number"
                        handleChange={setRequestedHours}
                        placeholder="e.g. 20"
                    />
                    {formErrors.requestedHours && <p className={styles.errorText}>{formErrors.requestedHours}</p>}
                </div>
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>From</label>
                    <div className={styles.periodPicker}>
                        <DropDown
                            options={MONTH_ABBR.map((m, i) => ({ name: m, value: i + 1 }))}
                            defaultVal={MONTH_ABBR[periodStartMonth - 1]}
                            onChange={(e) => setPeriodStartMonth(+e)}
                            dropDownwd={"flex-1"}
                        />
                        <DropDown
                            options={YEAR_OPTIONS.map((y, i) => ({ name: y, value: y }))}
                            defaultVal={periodStartYear}
                            onChange={(e) => setPeriodStartYear(+e)}
                            dropDownwd={"flex-1"}
                        />
                    </div>
                </div>
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>To</label>
                    <div className={styles.periodPicker}>
                        <DropDown
                            options={MONTH_ABBR.map((m, i) => ({ name: m, value: i + 1 }))}
                            defaultVal={MONTH_ABBR[periodEndMonth - 1]}
                            onChange={(e) => setPeriodEndMonth(+e)}
                            dropDownwd={"flex-1"}
                        />
                        <DropDown
                            options={YEAR_OPTIONS.filter((y) => y >= periodStartYear).map((y, i) => ({ name: y, value: y }))}
                            defaultVal={periodEndYear}
                            onChange={(e) => setPeriodEndYear(+e)}
                            dropDownwd={"flex-1"}
                        />
                    </div>
                    {formErrors.period && <p className={styles.errorText}>{formErrors.period}</p>}
                </div>
            </div>

            <div className={styles.formRow3}>
                {/* Change Request */}
                <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Change Request</label>
                    {changeRequest ? (
                        <div className={styles.selectedTag}>
                            <span>{changeRequest.label}</span>
                            <button onClick={() => { setChangeRequest(null); }}>×</button>
                        </div>
                    ) :
                        <>

                            <Searchable
                                searchText={(e) =>
                                    changRequestSearch(e, { projectId: projectID })
                                }
                                SelectText="change requests"
                                options={changeRequests}
                                defaultVal={changeRequest}
                                onChange={setChangeRequest}
                            />
                        </>

                    }
                </div>
            </div>

            {/* Notes */}
            <div className={`${styles.formGroup} mt-3`}>
                <label className={styles.formLabel}>Notes</label>
                <RichTextEditor
                    content={notes}
                    onChange={(value) => {
                        setNotes(value)
                    }}
                />
            </div>

            {/* Attachments */}
            <div className={`${styles.formGroup} mt-3`}>
                <EntityFileUpload
                    entityId={requestId}
                    entityType="ReshuffleRequest"
                    attachments={attachments}
                    onAttachmentsChange={() => {
                        if (requestId) {
                            reshuffleservice
                                .getSingleReshuffleRequest(projectID, requestId)
                                .then((reshuffleRequestData) => {
                                    setAttachments(reshuffleRequestData.attachments);
                                })
                                .catch((error) => {
                                    console.error(
                                        "Error fetching request data:",
                                        error
                                    );
                                });
                        }
                    }}
                    disabled={!requestId}
                />
            </div>
        </div>
    );

    // ── Step 2 ──────────────────────────────────────────────────────────────────
    const renderStep2 = () => {
        const hasSystemOptions = (options?.systemOptions || []).length > 0;
        const hasPool = (options?.resourcePool || []).length > 0;
        const globalErrors = getGlobalErrors();
        const periodMonths = generatePeriodMonths();

        return (
            <div className={styles.step2Container}>
                <button className={styles.backBtn} onClick={() => setWizardStep(1)}>
                    <div className="d-flex align-items-center gap-1">
                        <ArrowLeft size={16} />
                        Back to Request Info
                    </div>
                </button>
                <div className={styles.stepIndicator}>
                    <div className={`${styles.stepDot} ${styles.done}`}><span className={styles.stepCheck}><Check size={16} /></span></div>
                    <div className={styles.stepLine} />
                    <div className={`${styles.stepDot} ${styles.active}`}>2</div>
                    <div className={styles.stepLine} />
                    <div className={styles.stepDot}>3</div>
                </div>

                <div className={styles.step2Layout}>
                    {/* Left panel – System options */}
                    <div className={styles.optionsPanel}>
                        <div className={styles.panelTitle}>System Options</div>
                        {!hasSystemOptions && !hasPool && (
                            <div className={styles.infoBanner}>
                                No available donors found for the selected period. All project resources are either fully allocated or have no forecast hours in this period.
                            </div>
                        )}
                        {!hasSystemOptions && hasPool && (
                            <div className={styles.infoBannerInfo}>
                                No automatic options available. You can manually configure the request.
                            </div>
                        )}
                        {hasSystemOptions && (options.systemOptions || []).map((opt) => (
                            <div
                                key={opt.optionIndex}
                                className={`${styles.optionCard} ${selectedOptionIdx === opt.optionIndex ? styles.optionCardSelected : ""}`}
                                onClick={() => handleSelectOption(opt)}
                            >
                                <div className={styles.optionCardHeader}>
                                    {/* <span className={styles.optionTypeBadge}>{opt.optionType === "OneToOne" ? "1-to-1" : "Many-to-1"}</span> */}
                                    {opt.isCostNeutral && <span className={styles.costNeutralBadge}>Cost Neutral</span>}
                                    <span className={styles.optionHoursBadge}>{opt.totalTargetHoursGained} hrs gained</span>
                                </div>
                                <table className={styles.optionBreakdownTable}>
                                    <thead>
                                        <tr><th>Period</th><th>Donor</th><th>Donor</th><th>Target</th><th>Donor Rate</th><th>Target Rate</th></tr>
                                    </thead>
                                    <tbody>
                                        {(opt.items || []).map((item, i) => (
                                            <tr key={i}>
                                                <td>{formatMonthYear(item.year, item.month)}</td>
                                                <td>{renderUserHoverCell(item.donorResource, 18)}</td>
                                                <td>{`-${displayDecimalFormatted(setting, Number(item.donorHoursReduced))}`}</td>
                                                <td>{`+${displayDecimalFormatted(setting, Number(item.targetHoursAdded))}`}</td>
                                                <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.donorForecastHourlyCostRate))}`}</td>
                                                <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.targetForecastHourlyCostRate))}`}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <button className={styles.selectOptionBtn} onClick={() => handleSelectOption(opt)}>
                                    Select this option
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Right panel – Editable grid */}
                    <div className={styles.editPanel}>
                        <div className={styles.panelTitle}>Configure Request</div>

                        {globalErrors.length > 0 && (
                            <div className={styles.errorBanner}>
                                {globalErrors.map((e, i) => <p key={i}>{e.message}</p>)}
                            </div>
                        )}

                        {validation && !validation.preview?.isCostNeutral && validation.preview?.totalCostDelta !== undefined && (
                            <div className={styles.costWarningBanner}>
                                This reshuffle will change the project forecasted cost by{" "}
                                {validation.preview.totalCostDelta >= 0 ? "+" : ""}
                                {Number(validation.preview.totalCostDelta).toFixed(2)}
                            </div>
                        )}

                        {editableItems.length === 0 ? (
                            <div className={styles.emptyGrid}>Select a system option or add items manually.</div>
                        ) : (
                            <div className={styles.editableGrid}>
                                <div className={styles.gridHeader}>
                                    <span>Month</span>
                                    <span>Donor</span>
                                    <span></span>
                                    <span>
                                        Hours
                                        <Tooltip content={"Hours to be deducted from the donor resource to fund this transfer"} relationship="label" positioning="above-start" withArrow>
                                            <span className="cursor-pointer">{` ?`}</span>
                                        </Tooltip>
                                    </span>
                                    <span></span>
                                </div>
                                {editableItems.map((item) => {
                                    const pool = getPoolForMonth(item.year, item.month);
                                    const rowErrors = getRowErrors(item.donorResource?.id, item.year, item.month);
                                    const rowWarnings = getRowWarnings(item.donorResource?.id, item.year, item.month);
                                    const resourceData = getResourceDataFromPool(pool, item.donorResource?.id);
                                    return (
                                        <div key={item.tempId} className={styles.gridRow}>
                                            <div className={styles.gridCell}>
                                                <DropDown
                                                    options={periodMonths}
                                                    defaultVal={formatMonthYear(item.year, item.month)}
                                                    onChange={(e) => {
                                                        if (e) {
                                                            const [y, m] = e.split("-").map(Number);
                                                            updateEditableItem(item.tempId, "year", y);
                                                            updateEditableItem(item.tempId, "month", m);
                                                        }
                                                    }}
                                                    dropDownwd={"flex-1"}
                                                />
                                            </div>
                                            <div className={styles.gridCell}>
                                                {/* {renderUserHoverCell(item.donorResource, 18)} */}
                                                {<div className={styles.donorSelectWrapper}>
                                                    <DropDown
                                                        options={pool.map((r) => (
                                                            {
                                                                name: `${r.resource?.name} (${displayDecimalFormatted(setting, Number(r.availableFormatedHours || 0) )} hrs)`,
                                                                value: r.resource?.id,
                                                            }
                                                        ))}
                                                        defaultVal={resourceData?.resource ? renderUserHoverCell(resourceData?.resource, 18) : ""}
                                                        onChange={(e) => {
                                                            const sel = e ? pool.find((r) => r.resource?.id === e) : null;
                                                            updateEditableItem(item.tempId, "donorResource", sel?.resource || null);
                                                        }}
                                                        placeholder="Select Donor"
                                                        dropDownwd={"flex-1"}
                                                    />

                                                </div>}
                                            </div>
                                            <div className={styles.gridCell}>
                                                <Tooltip content={"Available hours expressed as target resource hours using cost rate based conversion"} relationship="label" positioning="above-start" withArrow>
                                                    <p
                                                        className={`
                                                            text-center py-[9px] rounded-md me-2 Input_wrapper position-relative`}
                                                        style={{
                                                            backgroundColor: Number(resourceData?.availableFormatedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c101f" : "#dc26261f",
                                                            color: Number(resourceData?.availableFormatedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c10" : "#dc2626",
                                                        }}
                                                    >
                                                        {`${displayDecimalFormatted(
                                                            setting,
                                                            Number(resourceData?.availableFormatedHours || 0)
                                                        )}`}
                                                    </p>
                                                </Tooltip>

                                            </div>
                                            <div className={styles.gridCell}>
                                                <Input
                                                    inputwd="flex-1"
                                                    value={item.targetHoursAdded}
                                                    min={0.1}
                                                    type="number"
                                                    handleChange={(value) => updateEditableItem(item.tempId, "targetHoursAdded", value)}
                                                    placeholder=""
                                                />

                                            </div>
                                            <div className={`${styles.gridCell}`}>
                                                <X className="cursor-pointer" color="#dc2626" size={16} onClick={() => removeEditableItem(item.tempId)} />
                                            </div>
                                            {rowErrors.length > 0 && rowErrors.map((error, index) => (

                                                <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.errorBanner}`}>
                                                    {error.message}{error.suggestedHours != null ? ` Max: ${error.suggestedHours} hrs` : ""}
                                                </div>
                                            ))}
                                            {Number(resourceData?.availableFormatedHours || 0) < Number(item.donorHoursReduced || 0) &&

                                                <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                                                    {`Available hours for this resource in this period is ${displayDecimalFormatted(
                                                        setting,
                                                        Number(resourceData?.availableFormatedHours || 0)
                                                    )} hrs. Consider reducing donor hours or selecting a different donor.`}
                                                </div>
                                            }
                                            {rowWarnings.length > 0 && rowWarnings.map((warning, index) => (

                                                <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                                                    {warning.message}{warning.suggestedHours != null ? ` Max: ${warning.suggestedHours} hrs` : ""}
                                                </div>
                                            ))}

                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        <button className={styles.addRowBtn} onClick={addEditableItem}>+ Add Row</button>
                    </div>
                </div>
            </div>
        );
    };

    // ── Step 3 ──────────────────────────────────────────────────────────────────
    const renderStep3 = () => (
        <div className={styles.wizardStep}>
            <button className={styles.backBtn} onClick={() => setWizardStep(2)}>
                <div className="d-flex align-items-center gap-1">
                    <ArrowLeft size={16} />
                    Back to Configuration
                </div>
            </button>
            <div className={styles.stepIndicator}>
                <div className={`${styles.stepDot} ${styles.done}`}><span className={styles.stepCheck}><Check size={16} /></span></div>
                <div className={styles.stepLine} />
                <div className={`${styles.stepDot} ${styles.done}`}><span className={styles.stepCheck}><Check size={16} /></span></div>
                <div className={styles.stepLine} />
                <div className={`${styles.stepDot} ${styles.active}`}>3</div>
            </div>

            {validation?.preview && <ReshuffleSummaryPreview preview={validation.preview} />}
        </div>
    );

    // ── Edit mode ────────────────────────────────────────────────────────────────
    const renderEditMode = () => {
        const showGetOptionsBtn = editableItems.length === 0;
        const globalErrors = getGlobalErrors();
        const pool = options?.resourcePool || [];
        const hasOptions = options ? true : false;
        const periodMonths = generatePeriodMonths();

        return (
            <div className={styles.editModeContainer}>
                {/* Read-only header */}
                <div className={styles.summaryHeader}>
                    <div className={styles.summaryCell}>
                        <p className={styles.summaryTitle}>
                            {renderUserHoverCell(targetResource)}
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        <p className={styles.summarySubtitle}>
                            {formatPeriodMonthYear(periodStartMonth, periodStartYear, periodEndMonth, periodEndYear)}
                            <span className={styles.summaryStatusBadge}>
                                {requestedHours} hrs
                            </span>
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        {status !== undefined && (
                            <span className={styles.summaryStatusBadge}>
                                {getReshuffleStatusLabel(status)}
                            </span>
                        )}
                    </div>
                </div>


                <div className={styles.formRow3}>
                    {/* Change Request */}
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Change Request</label>
                        {changeRequest ? (
                            <div className={styles.selectedTag}>
                                <span>{changeRequest.label}</span>
                                <button onClick={() => { setChangeRequest(null); }}>×</button>
                            </div>
                        ) :
                            <>

                                <Searchable
                                    searchText={(e) =>
                                        changRequestSearch(e, { projectId: projectID })
                                    }
                                    SelectText="change requests"
                                    options={changeRequests}
                                    defaultVal={changeRequest}
                                    onChange={setChangeRequest}
                                />
                            </>

                        }
                    </div>
                </div>

                {/* Get Options section (only when items cleared) */}
                {showGetOptionsBtn && (
                    <div className={styles.getOptionsSection}>
                        <p className={styles.getOptionsHint}>All items cleared. Fetch new options to repopulate.</p>
                        <button
                            className={styles.getOptionsBtn}
                            disabled={optionsLoading}
                            onClick={() => targetResource && handleGetOptions()}
                        >
                            {optionsLoading ? <Spinner size="sm" animation="border" /> : "Get Options"}
                        </button>
                        {options && (options.systemOptions || []).length > 0 && (

                            <div className={styles.optionsList}>
                                {(options.systemOptions || []).map((opt) => (
                                    <div
                                        key={opt.optionIndex}
                                        className={`${styles.optionCard} ${selectedOptionIdx === opt.optionIndex ? styles.optionCardSelected : ""}`}
                                        onClick={() => handleSelectOption(opt)}
                                    >
                                        <div className={styles.optionCardHeader}>
                                            {/* <span className={styles.optionTypeBadge}>{opt.optionType === "OneToOne" ? "1-to-1" : "Many-to-1"}</span> */}
                                            {opt.isCostNeutral && <span className={styles.costNeutralBadge}>Cost Neutral</span>}
                                            <span className={styles.optionHoursBadge}>{opt.totalTargetHoursGained} hrs gained</span>
                                        </div>
                                        <table className={styles.optionBreakdownTable}>
                                            <thead>
                                                <tr><th>Period</th><th>Donor</th><th>Donor</th><th>Target</th><th>Donor Rate</th><th>Target Rate</th></tr>
                                            </thead>
                                            <tbody>
                                                {(opt.items || []).map((item, i) => (
                                                    <tr key={i}>
                                                        <td>{formatMonthYear(item.year, item.month)}</td>
                                                        <td>{renderUserHoverCell(item.donorResource, 18)}</td>
                                                        <td>{`-${displayDecimalFormatted(setting, Number(item.donorHoursReduced))}`}</td>
                                                        <td>{`+${displayDecimalFormatted(setting, Number(item.targetHoursAdded))}`}</td>
                                                        <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.donorForecastHourlyCostRate))}`}</td>
                                                        <td>{`${getAccountCurrency(setting)} ${displayDecimalFormatted(setting, Number(item.targetForecastHourlyCostRate))}`}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                        <button className={styles.selectOptionBtn} onClick={() => handleSelectOption(opt)}>
                                            Select this option
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {/* Global errors */}
                {globalErrors.length > 0 && (
                    <div className={styles.globalErrorBanner}>
                        {globalErrors.map((e, i) => <p key={i}>{e.message}</p>)}
                    </div>
                )}

                {validation && !validation.preview?.isCostNeutral && validation.preview?.totalCostDelta !== undefined && (
                    <div className={styles.costWarningBanner}>
                        This reshuffle will change the project forecasted cost by{" "}
                        {validation.preview.totalCostDelta >= 0 ? "+" : ""}
                        {Number(validation.preview.totalCostDelta).toFixed(2)}
                    </div>
                )}

                {/* Editable grid */}
                {editableItems.length === 0 ? (
                    <div className={styles.emptyGrid}>Select a system option or add items manually.</div>
                ) : (
                    <div className={styles.editableGrid}>
                        <div className={styles.gridHeader}>
                            <span>Month</span>
                            <span>Donor</span>
                            {hasOptions && <span></span>}
                            <span>
                                Hours
                                <Tooltip content={"Hours to be deducted from the donor resource to fund this transfer"} relationship="label" positioning="above-start" withArrow>
                                    <span className="cursor-pointer">{` ?`}</span>
                                </Tooltip>
                            </span>
                            <span></span>
                        </div>
                        {editableItems.map((item) => {
                            const pool = getPoolForMonth(item.year, item.month);
                            const rowErrors = getRowErrors(item.donorResource?.id, item.year, item.month);
                            const rowWarnings = getRowWarnings(item.donorResource?.id, item.year, item.month);
                            const resourceData = getResourceDataFromPool(pool, item.donorResource?.id);
                            return (
                                <div key={item.tempId} className={styles.gridRow}>
                                    <div className={styles.gridCell}>
                                        {
                                            hasOptions ?
                                                <DropDown
                                                    options={periodMonths}
                                                    defaultVal={formatMonthYear(item.year, item.month)}
                                                    onChange={(e) => {
                                                        if (e) {
                                                            const [y, m] = e.split("-").map(Number);
                                                            updateEditableItem(item.tempId, "year", y);
                                                            updateEditableItem(item.tempId, "month", m);
                                                        }
                                                    }}
                                                    dropDownwd={"flex-1"}
                                                />
                                                :
                                                <span className="text-sm">{formatMonthYear(item.year, item.month)}</span>
                                        }
                                    </div>
                                    <div className={styles.gridCell}>
                                        {/* {renderUserHoverCell(item.donorResource, 18)} */}
                                        <div className={styles.donorSelectWrapper}>
                                            {hasOptions ?
                                                <DropDown
                                                    options={pool.map((r) => (
                                                        {
                                                            name: `${r.resource?.name} (${displayDecimalFormatted( setting, Number(r.availableFormatedHours || 0) )} hrs)`,
                                                            value: r.resource?.id,
                                                        }
                                                    ))}
                                                    defaultVal={resourceData?.resource ? renderUserHoverCell(resourceData?.resource, 18) : ""}
                                                    onChange={(e) => {
                                                        const sel = e ? pool.find((r) => r.resource?.id === e) : null;
                                                        updateEditableItem(item.tempId, "donorResource", sel?.resource || null);
                                                    }}
                                                    placeholder="Select Donor"
                                                    dropDownwd={"flex-1"}
                                                />
                                                :
                                                <span>{item.donorResource ? renderUserHoverCell(item.donorResource, 18) : "No donor selected"}</span>
                                            }
                                        </div>
                                    </div>
                                    {hasOptions &&
                                        <div className={styles.gridCell}>
                                            <Tooltip content={"Available hours expressed as target resource hours using cost rate based conversion"} relationship="label" positioning="above-start" withArrow>
                                                <p
                                                    className={`
                                                            text-center py-[9px] rounded-md me-2 Input_wrapper position-relative`}
                                                    style={{
                                                        backgroundColor: Number(resourceData?.availableFormatedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c101f" : "#dc26261f",
                                                        color: Number(resourceData?.availableFormatedHours || 0) >= Number(item.donorHoursReduced || 0) ? "#107c10" : "#dc2626",
                                                    }}
                                                >
                                                    {`${displayDecimalFormatted(
                                                        setting,
                                                        Number(resourceData?.availableFormatedHours || 0)
                                                    )}`}
                                                </p>
                                            </Tooltip>

                                        </div>
                                    }
                                    <div className={styles.gridCell}>
                                        {hasOptions ?
                                            <Input
                                                inputwd="flex-1"
                                                value={item.targetHoursAdded}
                                                min={0.1}
                                                type="number"
                                                handleChange={(value) => updateEditableItem(item.tempId, "targetHoursAdded", value)}
                                                placeholder=""
                                            />
                                            :
                                            <span>{displayDecimalFormatted(setting, Number(item.targetHoursAdded))}</span>
                                        }

                                    </div>
                                    <div className={`${styles.gridCell}`}>
                                        <X className="cursor-pointer" color="#dc2626" size={16} onClick={() => removeEditableItem(item.tempId)} />
                                    </div>
                                    {rowErrors.length > 0 && rowErrors.map((error, index) => (

                                        <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.errorBanner}`}>
                                            {error.message}{error.suggestedHours != null ? ` Max: ${error.suggestedHours} hrs` : ""}
                                        </div>
                                    ))}
                                    {hasOptions && Number(resourceData?.availableFormatedHours || 0) < Number(item.donorHoursReduced || 0) &&

                                        <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                                            {`Available hours for this resource in this period is ${displayDecimalFormatted(
                                                setting,
                                                Number(resourceData?.availableFormatedHours || 0)
                                            )} hrs. Consider reducing donor hours or selecting a different donor.`}
                                        </div>

                                    }
                                    {rowWarnings.length > 0 && rowWarnings.map((warning, index) => (

                                        <div className={`${styles.gridCell} ${styles.fullWidth} ${styles.infoBanner} mt-1`}>
                                            {warning.message}{warning.suggestedHours != null ? ` Max: ${warning.suggestedHours} hrs` : ""}
                                        </div>
                                    ))}

                                </div>
                            );
                        })}
                    </div>
                )}
                {
                    hasOptions && <button className={styles.addRowBtn} onClick={addEditableItem}>+ Add Row</button>
                }


                {/* Notes */}
                <div className={`${styles.formGroup} mt-3`}>
                    <label className={styles.formLabel}>Notes</label>
                    <RichTextEditor
                        content={notes}
                        onChange={(value) => { setNotes(value) }}
                        loading={directModalLoading}
                    />
                </div>

                {/* Attachments */}
                <div className={`${styles.formGroup} mt-3`}>
                    <EntityFileUpload
                        entityId={requestId}
                        entityType="ReshuffleRequest"
                        attachments={attachments}
                        onAttachmentsChange={() => {
                            if (requestId) {
                                reshuffleservice
                                    .getSingleReshuffleRequest(projectID, requestId)
                                    .then((reshuffleRequestData) => {
                                        setAttachments(reshuffleRequestData.attachments);
                                    })
                                    .catch((error) => {
                                        console.error(
                                            "Error fetching request data:",
                                            error
                                        );
                                    });
                            }
                        }}
                        disabled={!requestId}
                    />
                </div>
            </div>
        );
    };

    // ── Main form body (dispatched to Table.jsx modal) ──────────────────────────
    const formBody = () => {
        if (modalMode === "edit") return renderEditMode();
        switch (wizardStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return renderStep1();
        }
    };

    // ── View modal body ─────────────────────────────────────────────────────────
    const renderViewBody = () => {
        if (directModalLoading) {
            return <div className={styles.loadingState}><Spinner animation="border" /></div>;
        }
        if (!viewDetail) return null;
        const { targetResource: tr, periodStartYear: sy, periodStartMonth: sm, periodEndYear: ey, periodEndMonth: em, requestedHours: rh, status, notes: n, reviewNotes: rn, items } = viewDetail;
        return (
            <div className={styles.viewContainer}>
                <div className={styles.summaryHeader}>
                    <div className={styles.summaryCell}>
                        <p className={styles.summaryTitle}>
                            {renderUserHoverCell(tr)}
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        <p className={styles.summarySubtitle}>
                            {formatPeriodMonthYear(sm, sy, em, ey)}
                            <span className={styles.summaryStatusBadge}>
                                {rh} hrs
                            </span>
                        </p>
                    </div>
                    <div className={styles.summaryCell}>
                        {status !== undefined && (
                            <span className={styles.summaryStatusBadge}>
                                {getReshuffleStatusLabel(status)}
                            </span>
                        )}
                    </div>
                </div>

                {items?.length > 0 && (
                    <table className={styles.itemsTable}>
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th>Donor</th>
                                <th>Donor Hrs</th>
                                <th>Target Hrs</th>
                                <th>Donor Cost Rate</th>
                                <th>Target Cost Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={i}>
                                    <td>{formatMonthYear(item.year, item.month)}</td>
                                    <td>{renderUserHoverCell(item.donorResource, 18)}</td>
                                    <td>{item.donorHoursReduced}</td>
                                    <td>{item.targetHoursAdded}</td>
                                    <td>{item.donorForecastHourlyCostRate}</td>
                                    <td>{item.targetForecastHourlyCostRate}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        );
    };

    // ── Review (approve/reject) body ────────────────────────────────────────────
    const renderReviewBody = () => (
        <div className={styles.reviewContainer}>
            <div className={styles.summaryHeader}>
                <div className={styles.summaryCell}>
                    <p className={styles.summaryTitle}>
                        {renderUserHoverCell(directModalRow?.targetResource)}
                    </p>
                </div>
                <div className={styles.summaryCell}>
                    <p className={styles.summarySubtitle}>
                        {formatPeriodMonthYear(directModalRow?.periodStartMonth, directModalRow?.periodStartYear, directModalRow?.periodEndMonth, directModalRow?.periodEndYear)}
                        <span className={styles.summaryStatusBadge}>
                            {directModalRow?.requestedHours} hrs
                        </span>
                    </p>
                </div>
                <div className={styles.summaryCell}>
                    {directModalRow?.status !== undefined && (
                        <span className={styles.summaryStatusBadge}>
                            {getReshuffleStatusLabel(directModalRow?.status)}
                        </span>
                    )}
                </div>
            </div>
            <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                    Review Notes{directModalMode === "reject" && <span className={styles.required}> *</span>}
                </label>
                <RichTextEditor
                        content={reviewNotes}
                        onChange={(value) => { setReviewNotes(value) }}
                        loading={directModalLoading}
                    />
                {reviewNotesError && <p className={styles.errorText}>{reviewNotesError}</p>}
            </div>
        </div>
    );

    // ── Summary modal body ──────────────────────────────────────────────────────
    const renderSummaryBody = () => {
        if (directModalLoading) return <div className={styles.loadingState}><Spinner animation="border" /></div>;
        if (!summaryData) return null;
        return <ReshuffleSummaryPreview preview={summaryData} />;
    };

    const handleSearchTrigger = (searchTerm) => {
        if ((!tableSearch && !searchTerm) || (tableSearch === searchTerm)) return;

        let pageNumber = pageNum;
        if (!tableSearch || (tableSearch && !searchTerm) || (tableSearch !== searchTerm)) {
            setData([]); //clear existing data
            pageNumber = 1; //reset to first page
        }

        setTableSearch(searchTerm);
        fetchData(pageNumber, perPageItems, searchTerm, !searchTerm);
    };

    const handleSearch = (filteredData) => {
        setData(filteredData);
    };

    const getModalWidth = () => {
        if (wizardStep === 1) return "500px";
        if (wizardStep === 2) return "1300px";
        if (wizardStep === 3) return "1300px";
        return null;
    };
    // ─────────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────────
    return (
        <ColumnConfigProvider tableName="Project-Reshuffle-Request">
            <div className="listingScreen position-relative">
                <Table
                    type="Reshuffle Request"
                    rows={data || []}
                    body={formBody()}
                    searchHandler={handleSearch}
                    saveBtnLabel={getSaveBtnLabel()}
                    cancelBtnLabel="Cancel"
                    setPageNum={setPageNum}
                    perPageItems={perPageItems}
                    setIsCreate={handleCreate}
                    setIsEdit={() => setModalMode("edit")}
                    setData={setData}
                    parentType="Project"
                    loading={loading}
                    modalLoading={pageLoading}
                    pagination={pagination}
                    isCreate={modalMode === "create"}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                    handleCancel={resetForm}
                    setSelectedRow={handleRowSelect}
                    handleAction={handleAction}
                    projectId={projectID}
                    triggerSearch={handleSearchTrigger}
                    isModalSaveBtnDisabled={isSaveBtnDisabled()}
                    modalWidth={getModalWidth()}
                />

                {/* ── View Modal ──────────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "view" && (
                    <Modal
                        title="Request Details"
                        body={renderViewBody()}
                        handleCancel={closeDirectModal}
                        handleSave={null}
                        footer={false}
                        size="large"
                    />
                )}

                {/* ── Summary Modal ───────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "summary" && (
                    <Modal
                        title="Request Summary"
                        body={renderSummaryBody()}
                        handleCancel={closeDirectModal}
                        handleSave={null}
                        footer={false}
                        size="large"
                        width={"1300px"}
                    />
                )}

                {/* ── Approve Modal ───────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "approve" && (
                    <Modal
                        title="Approve Request"
                        body={renderReviewBody()}
                        handleCancel={closeDirectModal}
                        handleSave={handleApproveConfirm}
                        saveBtnLabel="Approve"
                        cancelBtnLabel="Cancel"
                        isLoading={pageLoading}
                        size="small"
                    />
                )}

                {/* ── Reject Modal ────────────────────────────────────────────────── */}
                {showDirectModal && directModalMode === "reject" && (
                    <Modal
                        title="Reject Request"
                        body={renderReviewBody()}
                        handleCancel={closeDirectModal}
                        handleSave={handleRejectConfirm}
                        saveBtnLabel="Reject"
                        cancelBtnLabel="Cancel"
                        isLoading={pageLoading}
                        size="small"
                    />
                )}
            </div>
        </ColumnConfigProvider>
    );
};

export default ReshuffleRequest;
